import {
  Component,
  Input,
  Output,
  EventEmitter,
  ElementRef,
  ViewChild,
  HostBinding,
  HostListener,
  OnInit,
} from '@angular/core';

import { MetricsGridConfig } from './MetricsGridConfig';
import { IGridColumnGroup } from './IGridColumnGroup.interface';
import { AuthenticationService } from '../authentication/authentication.service';
import { FormatService } from '../query';
import { LocaleService } from '../services';
import { DateTime } from 'luxon';

/**
 * Format the last updated date.
 * Accepts either an ISO string or a Date because runtime API data can still arrive
 * as a string even when the corresponding TypeScript model is typed as Date.
 * @param input - The date to format, either as an ISO string or a Date object.
 * @param locale - Locale string such as en-US.
 */
export function getDateString(input: string | Date, locale: string): string {
  let date =
    typeof input === 'string'
      ? DateTime.fromISO(input, {
          zone: 'America/Los_Angeles',
        })
      : DateTime.fromJSDate(input, {
          zone: 'America/Los_Angeles',
        });

  // We had a problem where the ISO string had issues. This string "2026-04-30T21:19:58.92"
  // would cause an invalid DateTime.
  if (!date.isValid && typeof input === 'string') {
    // Fallback: parse using the target zone so offset-less timestamps are treated
    // deterministically as Los Angeles local time across clients.
    date = DateTime.fromISO(input, {
      zone: 'America/Los_Angeles',
    });
  }

  if (!date.isValid) {
    return '';
  }

  const formattedDate = date.setLocale(locale);
  const dateShort = formattedDate.toLocaleString(DateTime.DATE_SHORT);
  // https://moment.github.io/luxon/#/formatting
  //const dateTimeStr = date.toFormat('f').split(',').join('');
  const timeStr = formattedDate.toFormat('t');

  const zone = formattedDate.toFormat('ZZZZ');

  return `${dateShort} ${timeStr} (${zone})`;
}

@Component({
  selector: 'rdo-column-selector',
  styleUrls: ['./column-selector.component.scss'],
  template: `
    <div
      #dropdownbox
      class="dropdown column-selector__dropdown"
      (blur)="onBlur($event.target)"
    >
      <!-- Bootstrap has a .show class with !important on it. The inline-block is required. -->
      <a
        #dLabelz
        id="dLabel"
        class="btn btn-primary btn-inverse"
        data-target="#"
        data-toggle="dropdown"
        role="button"
        aria-haspopup="true"
        aria-expanded="false"
        data-bs-toggle="dropdown"
        style="display: inline-block !important;"
      >
        <i class="fa fa-fw fa-columns" [ngClass]="{ 'fa-lg': size === 'md' }">
        </i
        >&nbsp;{{ handleTranslation('main.core.tables.common.select_columns') }}
      </a>
      <ul
        class="dropdown-menu"
        [ngClass]="{ 'pull-right': !gridConfig?.enableDelete }"
        aria-labelledby="dLabel"
      >
        @for (g of gridConfig?.groups; track g) {
          @if (!g.locked || g.forceCanClose) {
            <li>
              <a
                (click)="toggleVisibility($event, g)"
                id="{{
                  g.title || g.columnSelectorTitle
                    | rdoPathToId: 'list-selector'
                }}"
              >
                <i class="fa fa-fw" [ngClass]="{ 'fa-check': g.visible }"></i>
                {{ handleTranslation(g.columnSelectorTitle || g.title) }}
              </a>
            </li>
          }
        }
      </ul>
    </div>
    @if (authenticationService._userInfoView.HasClientAccessToExportData) {
      <button
        id="download-excel-button"
        (click)="clickExcelDownload($event, null)"
        class="btn btn-success btn-inverse"
      >
        <i
          class="fa fa-fw fa-file-excel-o"
          [ngClass]="{ 'fa-lg': size === 'md' }"
        ></i
        >&nbsp; {{ 'main.core.tables.common.excel' | translate }}
      </button>
    }
    @if (gridConfig?.enableDelete) {
      <button
        [disabled]="gridConfig.disableDeleteButton"
        (click)="clickDelete($event)"
        class="btn btn-danger btn-inverse"
      >
        <i class="fa fa-fw fa-trash-o"></i>&nbsp;
        {{ 'main.core.tables.common.delete' | translate }}
      </button>
    }
  `,
  standalone: false,
})
export class ColumnSelectorComponent {
  @Input() gridConfig: MetricsGridConfig;
  @Input() size = 'xs';
  @Output() visibilityChanged: EventEmitter<IGridColumnGroup> =
    new EventEmitter<IGridColumnGroup>();
  // eslint-disable-next-line @angular-eslint/no-output-rename
  @Output('downloadExcelClick') excelClick = new EventEmitter();
  @Output() deleteClick: EventEmitter<void> = new EventEmitter<void>();

  @ViewChild('dLabelz') dLabelz: ElementRef;
  @ViewChild('dropdownbox') dropdownbox: ElementRef;

  @HostBinding('attr.tabindex') tabindex = '0';
  @HostListener('blur', ['$event.target']) onBlur(target) {
    // this.handleIE();
  }

  constructor(
    public authenticationService: AuthenticationService,
    private formatService: FormatService,
    private localeService: LocaleService
  ) {}

  private handleIE() {
    if (this.IsInternetExplorerOrEdge()) {
      this.asyncShowDropdown(true, () => {
        this.asyncShowDropdown(false, () => {});
      });
    }
  }

  private IsInternetExplorerOrEdge() {
    const ua = window.navigator.userAgent;
    const msie = ua.indexOf('MSIE ');
    const edg = ua.indexOf('Edge');

    return (
      msie > 0 || edg > 0 || !!navigator.userAgent.match(/Trident.*rv\:11\./) // eslint-disable-line no-useless-escape
    );
  }

  private asyncShowDropdown(show: boolean, next: () => unknown) {
    setTimeout(() => {
      if (show) {
        if (!this.dropdownbox.nativeElement.classList.contains('open')) {
          this.dLabelz.nativeElement.click();
          next();
        } else {
          this.asyncShowDropdown(show, next);
        }
      } else {
        if (this.dropdownbox.nativeElement.classList.contains('open')) {
          this.dLabelz.nativeElement.click();
          next();
        } else {
          this.asyncShowDropdown(show, next);
        }
      }
    }, 1);
  }

  toggleVisibility(e: any, group: IGridColumnGroup) {
    if (!this.IsInternetExplorerOrEdge()) {
      e.stopPropagation();
    }
    group.visible = !group.visible;
    this.visibilityChanged.next(group);
    if (this.IsInternetExplorerOrEdge()) {
      setTimeout(() => {
        this.dropdownbox.nativeElement.classList.remove('open');
        this.dropdownbox.nativeElement.classList.add('open');
      }, 1);
    }
  }

  clickExcelDownload(e: any, group: IGridColumnGroup) {
    e.stopPropagation();
    this.excelClick.next(group);
  }

  clickDelete(e: any) {
    e.stopPropagation();
    this.deleteClick.next();
  }

  handleTranslation(text: string): string {
    return this.formatService.translateAndFormat(text, false);
  }

  getSelectorId = (group: IGridColumnGroup): string => {
    return `${group.title.toLowerCase().split(' ').join('-')}-selector`;
  };

  getLastUpdatedLabel(): string {
    if (!this.gridConfig?.lastUpdated) {
      return '';
    }
    return this.formatService.translateAndFormat(
      'main.core.tables.common.last_updated'
    );
  }

  /*
     getLastUpdatedDateTime(): string {
        if (!this.gridConfig.lastUpdated) {
            return '';
        }
        const date = DateTime.fromISO(this.gridConfig.lastUpdated, { zone: 'America/Los_Angeles' });
        const dateShort = date.toLocal().toLocaleString(DateTime.DATE_SHORT);
        const timeSimple = date.toLocal().toLocaleString(date.toLocal().locale === 'en-US' ? DateTime.TIME_SIMPLE : DateTime.TIME_24_SIMPLE);
        const abbreviation = date.toLocal().setLocale(window.navigator.language).toFormat('ZZZZ');

        return ` ${dateShort} ${timeSimple} (${abbreviation})`;
    }
   */

  // TODO:  This method gets spammed with every render cycle.  Not fixing now, because on Beta I have
  //        this code broken into a separate component. Need to make sure that this logic is put into that
  //        component and unit tests updated.
  getLastUpdatedDateTime(): string {
    if (!this.gridConfig?.lastUpdated) {
      return '';
    }

    const locale = this.localeService.getLocale();

    return getDateString(this.gridConfig?.lastUpdated, locale);
  }
}
